import React, { useState, useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { EditReportRow, FlattenedEditRow, getEditReport } from "../services/ticketApiService";
import {
  TanStackDynamicTable,
} from "../components/TanStackDynamicTable";
import { ColumnDef, ColumnFiltersState, SortingState } from "@tanstack/react-table";
import { Download } from "lucide-react";
import * as XLSX from "xlsx";

const EditReportPage: React.FC = () => {
  const { isAdmin } = useUser();
  const { instance, accounts } = useMsal();

  const [data, setData] = useState<EditReportRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [tableState, setTableState] = useState<{
    sorting: SortingState;
    pagination: any;
    columnFilters: ColumnFiltersState;
  }>({
    sorting: [],
    pagination: { pageIndex: 0, pageSize: 50 },
    columnFilters: [],
  });

  const fetchData = async () => {
    if (!accounts[0]) return;
    setLoading(true);
    try {
      const page = tableState.pagination.pageIndex + 1;
      const result = await getEditReport(
        instance,
        accounts[0],
        page,
        tableState.pagination.pageSize,
      );
      setData(result.data);
      setTotalCount(result.totalCount);
    } catch (error) {
      console.error("Failed to load edit report:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tableState.pagination.pageIndex, tableState.pagination.pageSize]);

  const parseJson = (json: string | null): any[] => {
    if (!json) return [];
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [];
    }
  };

  const flattenRows = (tickets: EditReportRow[]): FlattenedEditRow[] => {
    const rows: FlattenedEditRow[] = [];
    for (const ticket of tickets) {
      const edits = parseJson(ticket.manualEditJson);
      if (edits.length === 0) {
        rows.push({
          id: ticket.id,
          vendorId: ticket.vendorId,
          ticketNumber: ticket.ticketNumber,
          changeField: "",
          oldValue: "",
          newValue: "",
          rawExtractionJson: ticket.rawExtractionJson,
          automatedResultJson: ticket.automatedResultJson,
        });
      } else {
        for (const edit of edits) {
          rows.push({
            id: ticket.id,
            vendorId: ticket.vendorId,
            ticketNumber: ticket.ticketNumber,
            changeField: edit.field ?? "",
            oldValue: edit.oldValue ?? "",
            newValue: edit.newValue ?? "",
            rawExtractionJson: ticket.rawExtractionJson,
            automatedResultJson: ticket.automatedResultJson,
          });
        }
      }
    }
    return rows;
  };

  const flattenedData = useMemo<FlattenedEditRow[]>(() => flattenRows(data), [data]);

  const exportToExcel = async () => {
    if (!accounts[0]) return;
    setExporting(true);
    try {
      // Fetch up to 1000 tickets for export
      const result = await getEditReport(instance, accounts[0], 1, 1000);
      const allFlattened = flattenRows(result.data);
      const worksheetData = allFlattened.map((row) => ({
        "Ticket #": row.ticketNumber ?? "",
        Vendor: row.vendorId ?? "",
        "Changed Field": row.changeField,
        "Old Value": row.oldValue,
        "New Value": row.newValue,
        "Raw Extraction": row.rawExtractionJson ?? "",
        "Automated Result": row.automatedResultJson ?? "",
      }));
      const worksheet = XLSX.utils.json_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Edit Report");
      XLSX.writeFile(workbook, "edit-report.xlsx");
    } catch (error) {
      console.error("Failed to export:", error);
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ColumnDef<FlattenedEditRow>[]>(
    () => [
      {
        id: "ticketNumber",
        header: "Ticket #",
        accessorKey: "ticketNumber",
        cell: ({ row, getValue }) => {
          const display = getValue() as string;
          return (
            <Link
              to={`/ticket/${row.original.id}`}
              className="text-primary hover:text-blue-600 dark:hover:text-blue-400 hover:underline font-bold transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {display}
            </Link>
          );
        },
      },
      {
        id: "vendorId",
        header: "Vendor",
        accessorKey: "vendorId",
      },
      {
        id: "changeField",
        header: "Changed Field",
        accessorKey: "changeField",
      },
      {
        id: "oldValue",
        header: "Old Value",
        accessorKey: "oldValue",
      },
      {
        id: "newValue",
        header: "New Value",
        accessorKey: "newValue",
      },
      {
        id: "rawExtractionJson",
        header: "Raw Extraction",
        accessorKey: "rawExtractionJson",
        cell: ({ getValue }) => {
          const val = getValue() as string | null;
          return (
            <div className="max-w-xs truncate text-xs font-mono text-slate-500" title={val ?? ""}>
              {val || "\u2014"}
            </div>
          );
        },
      },
      {
        id: "automatedResultJson",
        header: "Automated Result",
        accessorKey: "automatedResultJson",
        cell: ({ getValue }) => {
          const val = getValue() as string | null;
          return (
            <div className="max-w-xs truncate text-xs font-mono text-slate-500" title={val ?? ""}>
              {val || "\u2014"}
            </div>
          );
        },
      },
    ],
    [],
  );

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-xl font-bold text-slate-900 dark:text-white">Access Denied</h1>
        <p className="text-slate-500 mt-2">You do not have permission to view this page.</p>
        <Link to="/" className="text-primary hover:underline mt-4 block">Go to Dashboard</Link>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-slate-50 dark:bg-background-dark">
      <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Edit Report</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Tickets that were manually edited after document intelligence extraction. Use this to identify mapping rules that need to be added.
          </p>
        </div>
        <button
          onClick={exportToExcel}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors shadow-lg shadow-emerald-500/20"
        >
          <Download size={16} />
          {exporting ? "Exporting..." : "Export to Excel"}
        </button>
      </div>

      <div className="flex-1 overflow-hidden flex flex-col">
        <TanStackDynamicTable
          data={flattenedData}
          columns={columns}
          rowCount={totalCount}
          tableName="edit_report"
          onStateChange={setTableState}
          defaultPageSize={50}
          showPageSizeSelector={false}
        />
      </div>
    </div>
  );
};

export default EditReportPage;
